import { EvaluatorJudgmentOption, HumanEvaluatorRequest } from "@/types/app/evaluator";
import { ReturnTypeSelect } from "./ReturnTypeSelect";
import { InputLabel, Textarea } from "@components/molecules/FormikInput";
import Button from "@components/library/Button";
import EditableString, { EditableStringProps } from "@components/llama/EditableString";
import { ValenceTag } from "./ValenceTag";
import { PlusIcon, TrashIcon } from "@heroicons/react/solid";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { InlineMenu } from "@components/SimpleTable/InlineMenu";
import { ContextMenuItem } from "@components/library/ContextMenu";
import { ValenceInfoTooltip } from "./HumanEvaluatorFrames/ValenceInfoTooltip";
import { NumberJudgmentEditor } from "./NumberJudgmentEditor";

interface Props {
  draftSpec: HumanEvaluatorRequest;
  setDraftSpec: (spec: HumanEvaluatorRequest) => void;
}

export const HumanEvaluatorEditor = ({ draftSpec, setDraftSpec }: Props) => {
  const [animationParent] = useAutoAnimate({ duration: 100 });

  return (
    <div className="flex flex-col gap-32 rounded-ms bg-background-base-1 px-20 py-24 shadow-card-sm">
      <div className="w-[180px]">
        <ReturnTypeSelect
          evaluatorType="human"
          returnType={draftSpec.return_type}
          setReturnType={(returnType) =>
            setDraftSpec({ ...draftSpec, return_type: returnType } as HumanEvaluatorRequest)
          }
        />
      </div>

      {(draftSpec.return_type === "select" || draftSpec.return_type === "multi_select") && (
        <div className="flex flex-col gap-12">
          <div className="flex gap-8">
            <div className="grow">
              <InputLabel label="Options" name="options" />
            </div>
            <div className="flex w-[180px] items-center gap-8">
              <InputLabel label="Valence" name="valence" />
              <ValenceInfoTooltip />
            </div>
          </div>
          <div className="flex flex-col rounded-ms border border-stroke-base-3" ref={animationParent}>
            {draftSpec.options?.map((option, index) => (
              <div key={index} className="flex items-center border-b border-stroke-base-2 last:border-none">
                <div className="flex grow p-8">
                  <OptionNameTag
                    name={option.name}
                    setName={(name) => {
                      const newOptions = [...(draftSpec.options || [])];
                      newOptions[index] = { ...newOptions[index], name };
                      setDraftSpec({ ...draftSpec, options: newOptions });
                    }}
                  />
                </div>
                <div className="flex w-[180px] pr-8">
                  <div className="grow">
                    <ValenceTag
                      valence={option.valence}
                      setValence={(valence) => {
                        setDraftSpec({
                          ...draftSpec,
                          options: draftSpec.options?.map((o, i) => (i === index ? { ...o, valence } : o)),
                        });
                      }}
                    />
                  </div>
                  <InlineMenu>
                    <ContextMenuItem
                      withinDropdownMenu
                      key="delete"
                      disabled={draftSpec.options?.length === 1}
                      IconLeft={TrashIcon}
                      onClick={() => {
                        setDraftSpec({
                          ...draftSpec,
                          options: draftSpec.options?.filter((_, i) => i !== index),
                        });
                      }}
                    >
                      Delete
                    </ContextMenuItem>
                  </InlineMenu>
                  {/* <div>Delete</div> */}
                </div>
              </div>
            ))}
          </div>
          <div>
            <Button
              IconLeft={PlusIcon}
              elevated
              size={24}
              onClick={() => {
                const existingOptions = draftSpec.options || [];
                const newOption: EvaluatorJudgmentOption = {
                  name: `Option ${existingOptions.length + 1}`,
                  valence: "neutral",
                };
                setDraftSpec({
                  ...draftSpec,
                  options: [...existingOptions, newOption],
                });
              }}
            >
              Option
            </Button>
          </div>
        </div>
      )}

      {draftSpec.return_type === "number" && <NumberJudgmentEditor draftSpec={draftSpec} setDraftSpec={setDraftSpec} />}

      {/* We don't have to show anything for text - we only show the instructions box */}
      {draftSpec.return_type === "text" && <></>}

      <div>
        <Textarea
          rows={6}
          className="text-12-16 text-text-base-1"
          name="instructions"
          label="Instructions (optional)"
          description="Annotation guidelines for domain experts"
          value={draftSpec.instructions ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            // Set to null if empty string
            setDraftSpec({ ...draftSpec, instructions: value ? value : null });
          }}
        />
      </div>
    </div>
  );
};

interface OptionNameTagProps extends Omit<EditableStringProps, "value" | "setValue"> {
  name: string;
  setName?: (name: string) => void;
}

export const OptionNameTag = ({ name, setName }: OptionNameTagProps) => {
  return (
    <div className="rounded-ms border border-stroke-base-3 text-12-14 text-text-base-1">
      <EditableString hideIcon value={name} setValue={setName} placeholder="Option name" />
    </div>
  );
};
